import { memo, useCallback } from "react";
import { useUnistyles } from "react-native-unistyles";
import { usePathname } from "expo-router";
import { Text, Button } from "@src/components";
import { DialogModal } from "@src/components/DialogModal";
import { isWeb } from "@src/utils/web-helpers";
import { useSubmitUpsellRequest } from "@visioni/a3s-upsell/app";
import { logError } from "@src/features/error-logging";
import { trackEvent } from "@src/services/analytics/amplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";

interface MonitoringUpgradeDialogProps {
  visible: boolean;
  siteId: string;
  onClose: () => void;
  /**
   * Called once the upsell request has been successfully submitted to the
   * backend (which dedupes on existing open Leads). Callers can use this
   * to surface a confirmation modal; the upsell banner itself reflects
   * the new `pending` state automatically via cache invalidation.
   */
  onSendRequest: () => void;
}

/**
 * Confirmation dialog for the monitoring upgrade request. Submitting the
 * request fires the upsell mutation; on success the dialog dismisses and
 * lets the parent surface a "request sent" confirmation. On failure the
 * dialog shows an inline error so the user can retry or fall back to
 * direct contact via the banner's error state.
 */
export const MonitoringUpgradeDialog = memo(
  ({ visible, siteId, onClose, onSendRequest }: MonitoringUpgradeDialogProps) => {
    const { theme } = useUnistyles();
    const pathname = usePathname();
    const { submit, isPending, isError } = useSubmitUpsellRequest("monitoring", {
      conversionPage: pathname,
      siteId,
      onSuccess: (product) => trackEvent(ANALYTICS_EVENTS.MONITORING_UPGRADE_REQUESTED, { productInterest: product }),
    });

    const handlePress = useCallback(() => {
      submit()
        .then(() => onSendRequest())
        .catch(logError);
    }, [submit, onSendRequest]);

    return (
      <DialogModal
        visible={visible}
        onClose={onClose}
        title="Request Monitoring Upgrade"
        style={{ width: isWeb ? 500 : undefined }}
      >
        <Text textStyle="xs/regular" color="textMuted" mb={isError ? "xl" : "5xl"}>
          Upgrade to 24/7 ASIAL-accredited monitoring. Our team will contact you shortly to help you get set up.
        </Text>
        {isError && (
          <Text textStyle="xs/regular" mb="5xl" style={{ color: theme.colors.status.error }}>
            We couldn&apos;t send your request right now. Please try again or contact support directly.
          </Text>
        )}
        <Button
          label="Send request"
          variant="primary"
          variantColor="blue"
          size="md"
          loading={isPending}
          disabled={isPending}
          onPress={handlePress}
        />
      </DialogModal>
    );
  },
);
